import React, { useMemo, useRef, useState, useLayoutEffect } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import './Latex.css';

/*
 * LaTeX math via KaTeX.
 *
 *   import { InlineMath, BlockMath } from '../../Latex';
 *
 *   <p>
 *     The reward <InlineMath math="r(x, \hat{x})" /> trades off length and
 *     accuracy:
 *   </p>
 *   <BlockMath
 *     math="
 *       J(\theta) = \mathbb{E}_{\hat{x} \sim \pi_\theta(\cdot \mid x)}
 *         \left[ r(x, \hat{x}) \right]
 *     "
 *   />
 *
 * Write the TeX in the quoted `math` attribute exactly as you would in a
 * .tex file — single backslashes, braces and newlines are all fine, because
 * JSX attribute strings are taken verbatim (no \t-style escape processing).
 * The only character you can't use is the quote delimiter itself; LaTeX
 * never needs `"` (use `''` for closing quotes), but if you do, switch the
 * attribute to single quotes: math='...'.
 *
 * To splice a JS value into the TeX, use the `tex` template tag (backslashes
 * survive, unlike a normal template literal):
 *
 *   <BlockMath math={tex`\theta_{${i}} = ${value}`} />
 *
 * Render errors show in red instead of crashing the page.
 *
 * There is no .sty file on the web — its two jobs live here instead:
 *   - \newcommand macros: add them to MACROS below; every InlineMath and
 *     BlockMath across the whole site can use them.
 *   - visual styling (size, spacing): edit Latex.css.
 */

// Project-wide macros — the \newcommand half of a LaTeX .sty file.
const MACROS = {
  '\\E': '\\mathbb{E}',
  '\\R': '\\mathbb{R}',
  '\\KL': '\\mathrm{KL}',
  '\\norm': '\\left\\lVert #1 \\right\\rVert',
  '\\abs': '\\left\\lvert #1 \\right\\rvert',
};

// Tagged template that keeps backslashes raw while interpolating values.
export const tex = (strings, ...values) =>
  strings.raw.reduce(
    (out, s, i) => out + s + (i < values.length ? String(values[i]) : ''),
    ''
  );

const renderTex = (src, displayMode) =>
  katex.renderToString(src, {
    displayMode,
    throwOnError: false,
    strict: false,
    // Fresh copy per render: \gdef inside one equation otherwise leaks into
    // the shared table and every later equation on the page.
    macros: { ...MACROS },
  });

// Prefer the `math` prop; string children still work for backwards
// compatibility but need String.raw to protect backslashes.
const texSource = (math, children) =>
  math != null ? math : React.Children.toArray(children).join('');

export const InlineMath = ({ math, children }) => {
  const src = texSource(math, children);
  const html = useMemo(() => renderTex(src, false), [src]);
  const ref = useRef(null);
  const [pad, setPad] = useState('');

  // JSX deletes whitespace at source-line boundaries, so putting <InlineMath>
  // on its own line glues the math to the words around it. Recover the space
  // at runtime: pad whichever side directly touches a word character or
  // sentence punctuation, while still hugging commas, periods and brackets.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prev = el.previousSibling;
    const next = el.nextSibling;
    const prevChar =
      prev && prev.nodeType === Node.TEXT_NODE
        ? prev.textContent.slice(-1)
        : '';
    const nextChar =
      next && next.nodeType === Node.TEXT_NODE
        ? next.textContent.charAt(0)
        : '';
    const left = /[\w.,:;!?'"’”)\]]/.test(prevChar) ? ' latex-pad-left' : '';
    const right = /[\w'"‘“([]/.test(nextChar) ? ' latex-pad-right' : '';
    setPad(left + right);
  }, [src]);

  return (
    <span
      ref={ref}
      className={`latex-inline${pad}`}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export const BlockMath = ({ math, children }) => {
  const src = texSource(math, children);
  const html = useMemo(() => renderTex(src, true), [src]);
  // A span styled display:block (not a div) so a BlockMath dropped inside a
  // <p> — the natural way to write a display equation mid-paragraph — stays
  // valid HTML instead of splitting the paragraph.
  return (
    // eslint-disable-next-line react/no-danger
    <span className="latex-block" dangerouslySetInnerHTML={{ __html: html }} />
  );
};

export default BlockMath;
